import { useEffect, useMemo } from 'react';

import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { CompareOutlined, NavigateBefore } from '@mui/icons-material';
import { Button, FormControl, MenuItem, Select } from '@mui/material';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { useHistory, useLocation, useParams } from 'react-router';

import { useLocalizedFormatDate } from '@/modules/claims/summary/state';

import { ImageType, type TImagetype } from '../../../@types/global';
import { isExternalUserAtom } from '../../../atoms/atoms';
import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import { showPastVisitsAtom } from '../../../modules/annotator/atoms';
import { selectUnifiedIdAtom } from '../../../modules/patientList/atoms';
import { CheckButton } from '../../../modules/patientList/patientStrip/findings/CheckButton';
import { usePreferences } from '../../../react-query/usePreferences';
import routesConstants from '../../../routes/routes.constants';
import { theme } from '../../../theme/theme';
import {
  blueBlack400,
  blueBlack650,
  blueUltramarine050,
  blueUltramarine200,
  blueUltramarine300,
  blueUltramarine400,
  greyLight600,
  greyLight700,
  yellowOrange300,
} from '../../../theme/videa-style-tokens';
import { useIsEmbedded } from '../../../utils/hooks';
import { useVisits } from '../../../utils/hooks/useVisits';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { useMapImageTypeToLabel } from '../../../utils/string';
import { parsePathname } from '../path-utils';

import { selectedUnifiedIdAtom, selectedVisitAtom } from './atoms';

const ImageViewerMenuContainer = styled.div<{ condensed: boolean }>`
  flex-grow: 1;
  padding: 0.5em;
  display: flex;
  gap: 1em;
  justify-content: flex-start;
  .MuiSelect-select,
  .MuiSvgIcon-root {
    @media (max-width: 1200px) {
      height: 0.8em;
    }
    color: ${blueUltramarine050};
  }
  .MuiSelect-select {
    background: ${blueBlack650};
    padding: ${(props) => (props.condensed ? '0.5em' : '0.75em')};
    min-height: 1.25em;
    height: 1.25em;
  }
  .MuiInputBase-root {
    font-size: 1em;
    outline: none;
  }
  .compare-visits-button {
    color: ${theme.palette.primary.contrastText};
    display: flex;
    width: fit-content;
    margin-left: ${(props) => (props.condensed ? '1em' : '0.5em')};
  }
  .compare-visits-text {
    margin-left: 0.5em;
    text-transform: capitalize;
    font-family: ${theme.typography.fontFamily};
    text-align: left;
    line-height: 1.1em;
    font-size: 0.875em;
    max-width: 6ch;
    @media (max-width: 1200px) {
      font-size: 0.625em;
    }
  }
  .visit-source {
    display: inline-block;
    margin: 0 0.5em 0 0;
    padding: 0.0125em 0.25em;
    background-color: transparent;
    border-radius: 0.25em;
    color: white;
    font-size: 0.875em;
    margin-left: 0.5em;
    border: 1px solid ${blueBlack400};
  }
  .visit-image-type {
    display: none;
  }
  .warning {
    fill: ${yellowOrange300};
    margin: auto;
  }
`;

const StyledFormControl = styled(FormControl)`
  max-width: 13em;
`;

const StyledMenuItem = styled(MenuItem)`
  font-size: 0.875em;
  font-weight: 700;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;

  .MuiTypography-root {
    width: 11ch;
  }

  .visit-count {
    display: inline-block;
    border-radius: 0.5em;
    background-color: ${greyLight700};
    color: white;
    width: 1.5em;
    text-align: center;
    padding: 0.01em 0.05em;
    margin-left: 0.5em;
  }

  .visit-source {
    display: inline-block;
    margin: 0 0.5em 0 0;
    padding: 0.025em 0.25em;
    background-color: transparent;
    border: 1px solid ${blueBlack400};
    border-radius: 0.25em;
    color: ${blueBlack400};
    font-size: 0.875em;
    font-weight: 400;
  }

  .visit-image-type {
    display: inline-block;
    margin: 0 0.5em 0 0;
    padding: 0.025em 0.25em;
    background-color: ${greyLight600};
    border-radius: 0.25em;
    color: black;
    font-size: 0.875em;
    font-weight: 400;
  }

  .visit-image-type-count {
    display: inline-block;
    color: black;
    padding: 0 0.25em;
    font-weight: 700;
  }

  &:hover {
    background-color: ${blueUltramarine200};
    color: white;
    transition: background-color 0.2s ease-in-out;

    .visit-image-type {
      background-color: ${blueUltramarine300};
      border: transparent;
      color: white;
    }
    .visit-image-type-count {
      color: white;
    }
    .visit-source {
      background-color: transparent;
      border: 1px solid white;
      color: white;
    }
  }

  &.Mui-selected {
    background-color: ${blueUltramarine200};
    color: black;

    .visit-source {
      background-color: transparent;
      border: 1px solid white;
      color: white;
    }
    .visit-image-type {
      background-color: ${blueUltramarine400};
      border: transparent;
      color: white;
    }
    .visit-image-type-count {
      color: white;
    }
    &:hover {
      background-color: ${blueUltramarine300};
      color: white;

      .visit-source {
        color: white;
        border-color: white;
      }
    }
  }
`;

export const ImageViewerMenu = ({ condensed }: { condensed?: boolean }) => {
  const [selectedVisit, setSelectedVisit] = useAtom(selectedVisitAtom);
  const [, setSelectedUnifiedId] = useAtom(selectedUnifiedIdAtom);
  const [showPastVisits, setShowPastVisits] = useAtom(showPastVisitsAtom);
  const { trackEvent } = useTrackEvent();
  const { pathname, search } = useLocation();
  const history = useHistory();
  const isEmbedded = useIsEmbedded();
  const { patientId } = parsePathname(pathname, isEmbedded);
  const params = new URLSearchParams(search);
  const [isExternalUser] = useAtom(isExternalUserAtom);
  const [selectUnifiedId] = useAtom(selectUnifiedIdAtom);
  const { data: prefs } = usePreferences();
  const isHistoricalTriggerEnabled = prefs?.historicalTriggerEnabled;
  const { imageId: selectedImageId } = useParams<{
    id: string;
    imageId?: string;
  }>();

  const formatDate = useLocalizedFormatDate();

  const { allVisits, ddaiVisits, connectorVisits } = useVisits(patientId);
  const mapImageTypeToLabel = useMapImageTypeToLabel();
  useEffect(() => {
    let defaultVisitId = '';

    const visitId = params.get('visit-id');

    if (visitId) {
      const defaultId = allVisits.find((visit) => {
        if (!!visit && 'id' in visit) {
          return !!visit.id && visit.id.toString() === visitId;
        }
      });
      if (defaultId && 'id' in defaultId) {
        defaultVisitId = String(defaultId.id);
      } else {
        defaultVisitId = '';
      }
    } else if (
      selectedVisit &&
      allVisits.find((visit) => {
        if (!!visit && 'id' in visit) {
          return !!visit.id && visit.id.toString() === selectedVisit;
        }
      })
    ) {
      return;
    }

    // If no default visit and external user, default to ddai visit,
    // otherwise connector visit
    if (isExternalUser && !defaultVisitId && ddaiVisits && ddaiVisits[0]?.id) {
      defaultVisitId = ddaiVisits[0].id.toString();
    } else if (!defaultVisitId && connectorVisits && connectorVisits[0]?.id) {
      defaultVisitId = connectorVisits[0].id.toString();
    }
    const defaultVisit = allVisits.find(
      (visit) => visit.id && String(visit.id) === defaultVisitId,
    );

    !!defaultVisitId && setSelectedVisit(defaultVisitId.toString());
    params.delete('visit-id');

    const unifiedId = params.get('unified-id');
    !!unifiedId && setSelectedUnifiedId(unifiedId);
    params.delete('unified-id');

    history.replace({
      search: params.toString(),
      pathname: `${isEmbedded ? '/embedded' : ''}${routesConstants.ANNOTATOR}/${
        defaultVisit?.patientVideaId || patientId
      }${selectedImageId ? `/${selectedImageId}` : ''}`,
    });

    // We only want to auto select a visit when first landing on the Image Viewer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ddaiVisits, connectorVisits, setSelectedVisit, setSelectedUnifiedId]);

  const visitOptions = useMemo(() => {
    const createOptions = (visitList: any[]) =>
      visitList.map((visit, index) => {
        const date = new Date(visit.visitDate);
        return (
          <StyledMenuItem
            key={visit.id}
            value={visit.id.toString()}
            data-date={formatDate(date.toISOString(), true)}
            divider={
              !prefs?.enableMultiSource &&
              index === visitList.length - 1 &&
              visit.source !== 'DDAI' &&
              !!ddaiVisits &&
              ddaiVisits?.length > 0
            }
          >
            <VideaTypography variant="labelSmall">
              {formatDate(date.toISOString(), true)}
            </VideaTypography>
            {visit.source === 'DDAI' ? (
              <span className="visit-source">
                <Trans>DDAI</Trans>
              </span>
            ) : null}
            {visit.imageTypeCounts.map(
              (typeCount: { type: TImagetype; count: number }) => {
                const { type, count } = typeCount;
                if (type === ImageType.OTHER) return null;
                return (
                  <span
                    key={`${mapImageTypeToLabel[type]}-${count}`}
                    className="visit-image-type"
                  >
                    {mapImageTypeToLabel[type]}
                    <span className="visit-image-type-count">{count}</span>
                  </span>
                );
              },
            )}
          </StyledMenuItem>
        );
      });

    if (isExternalUser) {
      return createOptions(ddaiVisits || []);
    }

    if (prefs?.enableMultiSource) {
      return createOptions(allVisits || []);
    }

    const ddaiOptions = createOptions(ddaiVisits || []);
    const connectorOptions = createOptions(connectorVisits || []);
    return [...connectorOptions, ...ddaiOptions];
  }, [
    allVisits,
    ddaiVisits,
    connectorVisits,
    isExternalUser,
    prefs?.enableMultiSource,
    mapImageTypeToLabel,
    formatDate,
  ]);

  const handleChange = (value: string | null) => {
    setSelectedVisit(value);
    trackEvent(mixpanelEvents.switchVisits, {
      currentVisitId: value,
      selectedVisitId: selectedVisit,
      source: 'visitListSelect',
    });
    const newVisit = allVisits.find(
      (visit) => visit.id && String(visit.id) === value,
    );
    history.push(
      (isEmbedded ? '/embedded' : '') +
        `${routesConstants.ANNOTATOR}/${newVisit?.patientVideaId || patientId}`,
    );
  };
  if (!selectedVisit) {
    return null;
  }

  return (
    <ImageViewerMenuContainer
      className="visits-menu sentry-unmask"
      condensed={!!condensed}
      data-testid="visits-menu"
    >
      {!showPastVisits && (
        <StyledFormControl className="visit-select" fullWidth>
          <Select
            value={selectedVisit}
            onChange={(e) => handleChange(e.target.value)}
            size="small"
            MenuProps={{
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
          >
            {visitOptions}
            {isHistoricalTriggerEnabled && (
              <CheckButton unifiedId={selectUnifiedId} patientId={patientId} />
            )}
          </Select>
        </StyledFormControl>
      )}
      {allVisits.length > 1 && (
        <Button
          variant="text"
          className={classNames('compare-visits-button')}
          data-testid="compare-visits-button"
          onClick={() => {
            setShowPastVisits(!showPastVisits);
            trackEvent(mixpanelEvents.compareVisits);
          }}
        >
          {showPastVisits ? (
            <>
              <NavigateBefore />
              <span className="compare-visits-text">
                <Trans>Back to FMX</Trans>
              </span>
            </>
          ) : (
            <>
              {!prefs?.useSidebarRedesign && (
                <>
                  <CompareOutlined />
                  <span className="compare-visits-text">
                    {condensed ? (
                      <Trans>Compare</Trans>
                    ) : (
                      <Trans>Compare Visits</Trans>
                    )}
                  </span>
                </>
              )}
            </>
          )}
        </Button>
      )}
    </ImageViewerMenuContainer>
  );
};
